import { pathExists, readJson, isSameOrChild } from '@wix/cli-fs';
import type {
  StaticSiteBuildMetadataSchema,
  WixAstroConfig,
} from '@wix/cli-astro-definitions';
import {
  buildMetadataSchema,
  getBuildMetadataPath,
} from '@wix/cli-astro-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { resolve } from 'node:path';

export async function readBuildMetadata(
  projectFolder: string,
  config: WixAstroConfig
) {
  if (config.projectType === 'Site' && config.site?.outputDirectory != null) {
    const site = config.site;

    let clientDir: string | undefined;
    let serverDir: string | undefined;

    if (typeof site.outputDirectory === 'string') {
      clientDir = resolve(projectFolder, site.outputDirectory);
    } else {
      if (site.outputDirectory.client) {
        clientDir = resolve(projectFolder, site.outputDirectory.client);
      }
      if (site.outputDirectory.server) {
        serverDir = resolve(projectFolder, site.outputDirectory.server);
      }
    }

    if (!clientDir && !serverDir) {
      throw new CliError({
        code: CliErrorCode.BuildOutputMissing({
          reason:
            'Expected at least one site output directory to be configured.',
        }),
        cause: null,
      });
    }

    if (
      (serverDir && !isSameOrChild(projectFolder, serverDir)) ||
      (clientDir && !isSameOrChild(projectFolder, clientDir))
    ) {
      throw new CliError({
        code: CliErrorCode.SiteOutputDirectoryOutsideProject(),
        cause: null,
      });
    }

    const result: StaticSiteBuildMetadataSchema = {
      metaDataSource: 'WIX_CONFIG',
      serverDir,
      clientDir,
    };

    return result;
  }

  const buildMetadataPath = getBuildMetadataPath(projectFolder);

  if (!(await pathExists(buildMetadataPath))) {
    throw new CliError({
      code: CliErrorCode.BuildOutputMissing({
        reason: `"${buildMetadataPath}" build output file is missing.`,
      }),
      cause: null,
    });
  }

  const buildMetadataJson = await readJson(buildMetadataPath);
  const parseResult = buildMetadataSchema.safeParse(buildMetadataJson);

  if (parseResult.success) {
    return parseResult.data;
  }

  throw new CliError({
    code: CliErrorCode.BuildOutputMissing({
      reason: `"${buildMetadataPath}" can't be read.`,
    }),
    cause: parseResult.error,
  });
}
